import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useMutation } from '@apollo/client';
import LOGIN_MUTATION from '../../core/graphql/mutations/login';
import { toast } from 'react-toastify';
import { TokenManagerStore } from 'core/auth/TokenManager';

interface AdminLoginProps {
  onLoginSuccess?: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const history = useHistory();

  const [loginMutation] = useMutation(LOGIN_MUTATION, { errorPolicy: 'all' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      const result = await loginMutation({
        variables: {
          email: email.trim(),
          password,
        },
      });

      const err = result?.errors?.[0];
      if (err) {
        const msg = err?.message || 'Login failed. Please check your credentials.';
        setErrorMessage(msg);
        toast.error(msg);
        return;
      }

      const data = result?.data;
      if (data?.login?.token) {
        const token = data.login.token;
        // Store token (backend may not return refreshToken in login payload)
        localStorage.setItem('token', token);
        if (data.login.refreshToken) {
          localStorage.setItem('refreshToken', data.login.refreshToken);
        }
        // Store user info (backend returns user: { id, email, role })
        localStorage.setItem('user', JSON.stringify(data.login.user));

        // IMPORTANT: TokenValidation reads the token from cookies via TokenManagerStore,
        // so we must persist it here; otherwise login may appear to work but the app
        // immediately treats the user as unauthenticated on the next route.
        TokenManagerStore.setToken(token);
        TokenManagerStore.storeToken(token);

        // Check if user is admin (role is the only field we rely on here)
        const userRole = data.login.user?.role;
        if (userRole === 'admin' || userRole === 'ADMIN') {
          toast.success('Admin login successful!');
          if (onLoginSuccess) {
            onLoginSuccess();
          } else {
            history.push('/admin/dashboard');
          }
        } else {
          const msg = 'Access denied. Admin privileges required.';
          setErrorMessage(msg);
          toast.error(msg);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      } else {
        const msg = 'Login failed. No token returned.';
        setErrorMessage(msg);
        toast.error(msg);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const rawMsg =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.message ||
        error?.message;
      const isNetworkFailure =
        !rawMsg ||
        String(rawMsg).toLowerCase().includes('failed to fetch') ||
        String(rawMsg).toLowerCase().includes('network error') ||
        error?.networkError;
      const msg = isNetworkFailure
        ? 'Cannot reach the API. Start the backend: cd starfarm-backend && npm run dev'
        : (rawMsg || 'Login failed. Please check your credentials.');
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      style={{ backgroundColor: '#f5f5f5' }}
    >
      <Paper elevation={3} style={{ padding: 40, maxWidth: 400, width: '100%' }}>
        <Box textAlign="center" marginBottom={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Admin Login
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Sign in to access the admin panel
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            variant="outlined"
            required
            disabled={loading}
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            variant="outlined"
            required
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            style={{ marginTop: 24, marginBottom: 16 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>

          {errorMessage && (
            <Alert severity="error" style={{ marginTop: 16 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
        </form>

        <Box textAlign="center" mt={2}>
          <Typography variant="body2" color="textSecondary">
            Demo: admin@starfarm.com / admin123 (run backend seed_demo first)
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
